/**
 * Acceso a datos de la pantalla de Resumen.
 *
 * Endpoints consumidos. Los cuatro EXISTEN hoy y devuelven datos reales;
 * verificados contra el codigo del backend el 2026-08-28:
 *
 *   GET /api/v1/monitoring/summary?store_id=  -> src/api/v1/monitoring.py::get_system_summary
 *   GET /api/v1/monitoring/sync?store_id=     -> src/api/v1/monitoring.py::get_sync_status
 *   GET /api/v1/invoicing/status/{store_id}   -> src/api/v1/invoicing.py::get_invoicing_status
 *   GET /api/v1/stores/{store_id}/stats       -> src/api/v1/stores.py::get_store_stats
 *
 * El ultimo se pide desde `src/api/endpoints.ts` (ya existia). Los tres
 * primeros se piden aca.
 *
 * Los cuatro llevan `store_id` --en el path o en el query--, asi que
 * `src/api/deps.py::enforce_authentication` aplica tenencia sin que el handler
 * tenga que acordarse: si el usuario no tiene la tienda concedida, la respuesta
 * es 403 y la pantalla muestra "sin permiso", no una tarjeta en blanco.
 *
 * Por que los tipos estan escritos a mano
 * ---------------------------------------
 * `src/api/schema.gen.ts` esta generado y viejo: no conoce `/monitoring`
 * (comprobable: `grep -c SystemSummary src/api/schema.gen.ts` da 0) y los
 * handlers de facturacion no declaran `response_model`, asi que el OpenAPI los
 * describe como `{}`. Lo de abajo espeja a mano `src/schemas/monitoring.py` y
 * el dict que devuelve `get_invoicing_status`. **Es deuda, no diseno**: el dia
 * que se corra `npm run gen:api` con el backend actual, este bloque se cambia
 * por alias de `../../api/types`. Mismo precedente que
 * `screens/publication/api.ts` y `screens/invoicing/api.ts`.
 *
 * Por que se duplica el fetcher de `/invoicing/status` en vez de importar el de
 * `screens/invoicing/api.ts`
 * --------------------------------------------------------------------------
 * Esta pantalla es la ruta raiz y se importa de forma ESTATICA en
 * `src/components/routes.tsx` (las otras cinco entran por `lazyScreen`, o sea
 * en su propio chunk). Importar de `screens/invoicing/` arrastraria ese modulo
 * --y lo que el importe manana-- al bundle inicial, que es exactamente lo que
 * el code splitting evita. Son ocho lineas repetidas contra el costo de arranque
 * de todo el panel.
 */

import type { ApiClient, RequestOptions } from "../../api/client";

// ---------------------------------------------------------------------------
// Tipos (espejo de src/schemas/monitoring.py)
// ---------------------------------------------------------------------------

/**
 * Conteo de `products` de una tienda, desglosado.
 *
 * `other_status` existe para que `active + paused + closed + other_status ==
 * total` sea un invariante: `products.status` es texto libre sin CHECK y un
 * valor inesperado tiene que aparecer, no evaporarse.
 */
export interface ProductCounts {
  readonly total: number;
  readonly active: number;
  readonly paused: number;
  readonly closed: number;
  readonly other_status: number;
  /** `is_banned = TRUE`: quedan fuera de la publicacion entre tiendas. */
  readonly banned: number;
  /** Sin `ml_id`: filas que NO vinieron de una sincronizacion con ML. */
  readonly without_ml_id: number;
  readonly out_of_stock: number;
}

/** Rollup de `publication_jobs` de una tienda, en un rol (origen o destino). */
export interface PublicationCounts {
  readonly total: number;
  /** Los seis estados, siempre presentes (aunque valgan 0). */
  readonly by_status: Readonly<Record<string, number>>;
  /** `pending` + `running`. */
  readonly in_flight: number;
  /** Hablaron con ML y no se sabe como terminaron. Pide que alguien mire. */
  readonly needs_verification: number;
  readonly last_batch_id: string | null;
  readonly last_activity_at: string | null;
}

/** Una tienda y todo lo que se puede decir de ella sin salir de la base. */
export interface StoreSummary {
  readonly store_id: number;
  readonly name: string;
  readonly slug: string;
  readonly is_active: boolean;
  readonly sync_enabled: boolean;
  readonly ml_site_id: string;
  readonly ml_user_id: string | null;
  /**
   * Hay un `ml_access_token` guardado. El backend NO devuelve el token ni un
   * fragmento, y esto no dice que el token sirva: solo que la cuenta esta
   * vinculada.
   */
  readonly has_ml_tokens: boolean;
  readonly products: ProductCounts;
  readonly publication_as_source: PublicationCounts;
  readonly publication_as_target: PublicationCounts;
}

export interface MonitoringScope {
  readonly store_ids: readonly number[];
  readonly is_service: boolean;
}

export interface SystemSummary {
  readonly generated_at: string;
  readonly scope: MonitoringScope;
  readonly stores: readonly StoreSummary[];
}

/**
 * Lo que se sabe de la sincronizacion de una tienda.
 *
 * `last_product_change_at` es `MAX(products.updated_at)`, o sea un **limite
 * superior** de "cuando corrio la ultima sync", no la sync: un
 * `PATCH /products/{id}/banned` mueve el mismo timestamp. El backend lo nombra
 * asi a proposito y la pantalla lo rotula igual.
 */
export interface StoreSyncState {
  readonly store_id: number;
  readonly store_name: string;
  readonly sync_enabled: boolean;
  readonly has_ml_tokens: boolean;
  readonly products_total: number;
  readonly products_with_ml_id: number;
  readonly last_product_change_at: string | null;
  readonly oldest_product_change_at: string | null;
  readonly products_changed_recently: number;
  readonly recent_window_hours: number;
  /** `null` = no se pudo consultar el lock (Redis no contesto). */
  readonly running: boolean | null;
  readonly lock_key: string;
}

export interface SyncStatusResponse {
  readonly generated_at: string;
  readonly scope: MonitoringScope;
  /**
   * `false` mientras no exista una tabla de corridas. La pantalla lo usa para
   * NO dibujar un panel de "resultado de la ultima corrida" que no puede
   * llenar con datos reales.
   */
  readonly run_history_available: boolean;
  readonly run_history_note: string | null;
  readonly stores: readonly StoreSyncState[];
}

/**
 * Respuesta de `GET /api/v1/invoicing/status/{store_id}`.
 *
 * Los dos contadores miran `created_at`, o sea cuando se creo la FILA, no la
 * fecha fiscal del comprobante. Y son lo UNICO que hay: no existe todavia un
 * endpoint que devuelva el total de facturas de la tienda (ver
 * `screens/invoicing/api.ts`, que documenta el listado faltante).
 */
export interface InvoicingStatus {
  readonly store_id: number;
  readonly invoices_today: number;
  readonly invoices_last_7_days: number;
}

// ---------------------------------------------------------------------------
// Snapshots por tienda
// ---------------------------------------------------------------------------

/**
 * El estado de sincronizacion de UNA tienda, mas los dos campos de la respuesta
 * que no son por tienda y la pantalla necesita.
 *
 * `run_history_note` viaja hasta el componente en vez de reescribirse aca: es
 * el backend el que explica que no persiste las corridas, y copiar la frase
 * garantizaria que las dos versiones se separen el dia que exista la tabla.
 */
export interface StoreSyncSnapshot {
  readonly generated_at: string;
  readonly run_history_available: boolean;
  readonly run_history_note: string | null;
  readonly store: StoreSyncState;
}

/**
 * Error de "la respuesta vino bien pero no traia la tienda que pedimos".
 *
 * Es una clase propia y no un `Error` suelto porque el caso es real y tiene una
 * causa concreta: la tienda se borro entre que el selector la confirmo y que
 * esta pantalla pidio el resumen. Decirlo asi es mas util que un "no hay
 * datos", que mandaria a buscar el problema en el lugar equivocado.
 */
export class StoreMissingFromResponse extends Error {
  readonly storeId: number;

  constructor(storeId: number, endpoint: string) {
    super(
      `${endpoint} respondio sin la tienda ${storeId}. El backend acoto la ` +
        `respuesta a la tenencia del usuario: si la tienda existia hace un ` +
        `momento, puede haberse borrado o desactivado el acceso.`,
    );
    this.name = "StoreMissingFromResponse";
    this.storeId = storeId;
  }
}

// ---------------------------------------------------------------------------
// Llamadas
// ---------------------------------------------------------------------------

/**
 * Resumen de UNA tienda.
 *
 * El endpoint devuelve una lista (acotada a la tenencia); con `store_id` puesto
 * trae una sola entrada. La extraccion se hace aca para que los paneles reciban
 * la tienda garantizada y no tengan que repetir un `find(...)` que se puede
 * olvidar de comprobar el resultado.
 */
export async function getStoreSummary(
  client: ApiClient,
  storeId: number,
  options: RequestOptions = {},
): Promise<StoreSummary> {
  const response = await client.get<SystemSummary>("/monitoring/summary", {
    ...options,
    query: { store_id: storeId },
  });
  const entry = response.stores.find((store) => store.store_id === storeId);
  if (entry === undefined) {
    throw new StoreMissingFromResponse(storeId, "GET /monitoring/summary");
  }
  return entry;
}

/** Estado de sincronizacion de UNA tienda. */
export async function getStoreSyncSnapshot(
  client: ApiClient,
  storeId: number,
  options: RequestOptions = {},
): Promise<StoreSyncSnapshot> {
  const response = await client.get<SyncStatusResponse>("/monitoring/sync", {
    ...options,
    query: { store_id: storeId },
  });
  const entry = response.stores.find((store) => store.store_id === storeId);
  if (entry === undefined) {
    throw new StoreMissingFromResponse(storeId, "GET /monitoring/sync");
  }
  return {
    generated_at: response.generated_at,
    run_history_available: response.run_history_available,
    run_history_note: response.run_history_note,
    store: entry,
  };
}

/** Contadores de facturacion de una tienda (hoy y ultimos 7 dias). */
export function getInvoicingStatus(
  client: ApiClient,
  storeId: number,
  options: RequestOptions = {},
): Promise<InvoicingStatus> {
  return client.get<InvoicingStatus>(`/invoicing/status/${storeId}`, options);
}
